import { useState, useEffect } from "react";
import { FaAngleRight } from "react-icons/fa6";
import { MdOutlineKeyboardBackspace } from "react-icons/md";
import { IoSearchOutline } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import LoadingSpinner from "../../../../components/elements/LoadingSpinner";
import { BsEye } from "react-icons/bs";
import OrderModal from "../../../../components/modals/Restaurant/OrderModal";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { RootState } from "../../../../redux/store";
import {
  getAllRestaurantOrders,
  getRestaurantOrderDetails,
  clearCurrentOrderDetails,
  RestaurantOrderItem,
} from "../../../../redux/restaurantOrders/restaurantOrdersSlice";

const VendorOrder = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const {
    orders,
    currentOrderDetails,
    status,
    detailsStatus,
    error,
    totalCount,
  } = useAppSelector((state: RootState) => state.restaurantOrders);

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    dispatch(
      getAllRestaurantOrders({
        page: 1,
        limit: 100,
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (selectedOrderId) {
      dispatch(getRestaurantOrderDetails(selectedOrderId));
    }
  }, [dispatch, selectedOrderId]);

  const handleViewOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
  };

  const handleCloseModal = () => {
    setSelectedOrderId(null);
    dispatch(clearCurrentOrderDetails());
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "delivered":
        return "bg-[#008B501F] text-secondary";
      case "pending":
      case "preparing":
        return "text-yellow-600 bg-yellow-100";
      case "cancelled":
        return "text-red-500 bg-[#EA19191F]";
      case "ready":
        return "text-blue-600 bg-blue-100";
      case "in-progress":
        return "text-orange-600 bg-orange-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  const formatETA = (etaString: string) => {
    const eta = new Date(etaString);
    const now = new Date();
    const diff = eta.getTime() - now.getTime();
    const minutes = Math.max(0, Math.floor(diff / (1000 * 60)));
    return minutes > 0 ? `${minutes} mins` : "Now";
  };

  const isLoading = status === "loading";

  // Get filtered orders
  const getFilteredOrders = () => {
    if (!orders || orders.length === 0) return [];

    return orders.filter((order) => {
      // Status filter
      const statusMatch =
        statusFilter === "all" || order.status === statusFilter;

      // Search filter
      if (!searchTerm) return statusMatch;

      const searchLower = searchTerm.toLowerCase();
      const searchMatch =
        order.orderNumber?.toLowerCase().includes(searchLower) ||
        order.orderId?.toLowerCase().includes(searchLower) ||
        order.customerName?.toLowerCase().includes(searchLower) ||
        order.riderName?.toLowerCase().includes(searchLower);

      return statusMatch && searchMatch;
    });
  };

  const filteredOrders = getFilteredOrders();
  const restaurantName = "Restaurant Orders";

  return (
    <section className="font-mont">
      <div className="flex items-start gap-4">
        <button
          className="cursor-pointer p-2 rounded-full bg-[#008B5014]"
          onClick={() => navigate(-1)}
        >
          <MdOutlineKeyboardBackspace className="w-6 h-6" />
        </button>
        <article>
          <h1 className="text-2xl font-medium mb-2 inline-flex items-center gap-3">
            <span className="font-semibold">Restaurant</span> <FaAngleRight />
            <span className="font-semibold">{restaurantName} - Orders</span>
          </h1>
          <p className="text-sm">
            Order Management ({filteredOrders.length} of {orders.length} orders)
            {(searchTerm || statusFilter !== "all") && (
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                Filtered
              </span>
            )}
          </p>
        </article>
      </div>

      {/* Search and Filter Section */}
      <div className="my-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <IoSearchOutline className="w-5 h-5 absolute top-[0.6rem] left-3 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 pl-10 text-sm w-full focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary"
            placeholder="Search by order number, customer, or rider..."
          />
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
          >
            <option value="all">All bold</option>
            <option value="pending">Pending</option>
            <option value="preparing">Preparing</option>
            <option value="ready">Ready</option>
            <option value="in-progress">In Progress</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Clear Filters Button */}
        {(searchTerm || statusFilter !== "all") && (
          <button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
            }}
            className="text-sm text-secondary hover:text-secondary-dark underline"
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="min-h-[500px]">
        <table className="w-full">
          <thead className="font-normal">
            <tr className="bg-gray-100 text-left">
              <th className="p-3 font-medium">Order Number</th>
              <th className="p-3 font-medium">Customer</th>
              <th className="p-3 font-medium">Rider</th>
              <th className="p-3 font-medium">Total</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium">ETA</th>
              <th className="p-3 font-medium">Order Date</th>
              <th className="p-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="text-center py-8">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-red-500">
                  Error loading orders: {error}
                </td>
              </tr>
            ) : filteredOrders.length > 0 ? (
              filteredOrders.map((order: RestaurantOrderItem) => (
                <tr
                  key={order.orderId}
                  className="border text-sm hover:bg-gray-50"
                >
                  <td className="p-5">
                    <span className="font-medium">{order.orderNumber}</span>
                    <br />
                    <span className="text-xs text-gray-500">
                      {order.orderId.slice(-8)}
                    </span>
                  </td>
                  <td className="p-5">{order.customerName || "Anonymous"}</td>
                  <td className="p-5">{order.riderName || "Unassigned"}</td>
                  <td className="p-5">₦{order.totalAmount.toLocaleString()}</td>
                  <td className="p-5">
                    <span
                      className={`${getStatusColor(
                        order.status
                      )} text-center rounded-md p-2 text-xs font-medium capitalize`}
                    >
                      {order.status}
                    </span>
                  </td>
                  <td className="p-5">
                    <span className="text-sm">{formatETA(order.eta)}</span>
                  </td>
                  <td className="p-5">
                    <span className="text-xs text-gray-500">
                      {formatDate(order.orderDate)}
                    </span>
                  </td>
                  <td className="p-5">
                    <button
                      className="text-secondary border border-secondary rounded-md px-4 py-2 flex items-center gap-2 text-sm hover:bg-secondary hover:text-white transition-colors"
                      onClick={() => handleViewOrder(order.orderId)}
                      disabled={
                        detailsStatus === "loading" &&
                        selectedOrderId === order.orderId
                      }
                    >
                      <BsEye className="w-4 h-4" />
                      {detailsStatus === "loading" &&
                      selectedOrderId === order.orderId
                        ? "Loading..."
                        : "View"}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="text-center py-8 text-gray-500">
                  {searchTerm || statusFilter !== "all"
                    ? "No orders match your filters."
                    : "No orders found."}
                  {(searchTerm || statusFilter !== "all") && (
                    <div className="mt-2">
                      <button
                        onClick={() => {
                          setSearchTerm("");
                          setStatusFilter("all");
                        }}
                        className="text-secondary hover:underline text-sm"
                      >
                        Clear filters to see all orders
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {selectedOrderId && currentOrderDetails && (
        <OrderModal
          toggleModal={handleCloseModal}
          order={currentOrderDetails}
        />
      )}
    </section>
  );
};

export default VendorOrder;
